import {PER_PAGE, mesclarPorId, paginaCheia} from './paginacao';

export type CourseStatus = 'not_open' | 'open' | 'finished';

export interface Course {
  id: string;
  title: string;
  resume?: string | null;
  description?: string | null;
  courseStatus?: CourseStatus | string | null;
  poster?: {uri?: string | null} | null;
  cover?: {uri?: string | null} | null;
  nextUnit?: {id: string; title?: string | null} | null;
}

// Espelha o CourseStatus do backend. Curso nunca aberto nao ganha selo: o
// estado normal da lista nao precisa de rotulo.
const STATUS: Record<string, string> = {
  open: 'Em andamento',
  finished: 'Concluído',
};

export const statusDoCurso = (valor?: string | null): string => STATUS[valor ?? ''] ?? '';

// A conta configura settings.screens.courses.columnList, que vem como String.
// Fora de 1..4 a grade fica inutilizavel no celular, entao o valor e limitado.
const COLUNAS_PADRAO = 2;
const COLUNAS_MIN = 1;
const COLUNAS_MAX = 4;

export const parseColumnList = (valor?: string | null): number => {
  const numero = Number.parseInt(`${valor ?? ''}`.trim(), 10);

  if (!Number.isFinite(numero)) return COLUNAS_PADRAO;

  return Math.min(Math.max(numero, COLUNAS_MIN), COLUNAS_MAX);
};

export const posterDoCurso = (curso: Course): string | null =>
  curso.poster?.uri || curso.cover?.uri || null;

// A descricao do schema fala em 15 itens, mas o resolver usa o PER_PAGE
// compartilhado; confiar no valor real evita pedir uma pagina que nao existe.
export const PAGINA_CURSOS = PER_PAGE;

export const podeTerMais = (ultimaPagina: Course[]): boolean => paginaCheia(ultimaPagina, PAGINA_CURSOS);

export const mesclarCursos = (anterior: Course[], proxima: Course[]): Course[] =>
  mesclarPorId(anterior, proxima);

export interface Unit {
  id: string;
  title: string;
  resume?: string | null;
  description?: string | null;
  unitType?: string | null;
  completed?: boolean | null;
  video?: {uri?: string | null} | null;
  audio?: {uri?: string | null} | null;
  cover?: {uri?: string | null} | null;
}

export interface CourseModule {
  id: string;
  title: string;
  description?: string | null;
  order?: number | null;
  units?: Unit[] | null;
}

export interface CourseDetail extends Course {
  courseModules?: CourseModule[] | null;
  units?: Unit[] | null;
  trailer?: {uri?: string | null} | null;
}

const TIPOS_DE_AULA: Record<string, string> = {
  video: 'Vídeo',
  audio: 'Áudio',
  text: 'Texto',
  pdf: 'PDF',
  attachment: 'Anexo',
};

export const tipoDeAula = (unit: Unit): string => TIPOS_DE_AULA[`${unit.unitType ?? ''}`.toLowerCase()] ?? '';

export const iconeDeAula = (unit: Unit): string => {
  switch (`${unit.unitType ?? ''}`.toLowerCase()) {
    case 'video':
      return 'fa-circle-play';
    case 'audio':
      return 'fa-headphones';
    case 'pdf':
    case 'attachment':
      return 'fa-file-lines';
    default:
      return 'fa-book-open';
  }
};

// Um curso pode ter modulos ou aulas soltas. Quando nao ha modulo, as aulas
// viram um bloco unico sem titulo, para a tela ter sempre a mesma forma.
export const modulosDoCurso = (curso?: CourseDetail | null): CourseModule[] => {
  if (!curso) return [];

  const modulos = (curso.courseModules ?? []).filter((modulo) => (modulo.units ?? []).length > 0);
  if (modulos.length > 0) {
    return [...modulos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  const soltas = curso.units ?? [];
  if (soltas.length === 0) return [];

  return [{id: `curso-${curso.id}`, title: '', units: soltas}];
};

export const totalDeAulas = (curso?: CourseDetail | null): number =>
  modulosDoCurso(curso).reduce((soma, modulo) => soma + (modulo.units ?? []).length, 0);

export const aulasConcluidas = (curso?: CourseDetail | null): number =>
  modulosDoCurso(curso).reduce(
    (soma, modulo) => soma + (modulo.units ?? []).filter((unit) => unit.completed).length,
    0,
  );
